<script>
import { required, minLength } from "vuelidate/lib/validators";
import Customers from "@/services/Customers";
import validationMessages from "@/components/validations";

export default {
  components: {
    validationMessages,
  },
  props: {
    labelData: {
      type: Object,
      default: null,
    },
    mode: {
      type: String,
      default: "add",
    },
  },
  data() {
    return {
      csrf_token: localStorage.getItem("csrf_token"),
      showModal: false,
      tryingToSubmit: false,
      submitted: false,
      name: "",
      description: "",
      color: "#6c757d",
      is_crm_label: false,
      is_business_label: false,
      is_company_label: false,
      active: true,
    };
  },
  validations: {
    name: {
      required,
      minLength: minLength(2),
    },
  },
  watch: {
    labelData: {
      immediate: true,
      handler(newVal) {
        if (!newVal || this.mode !== "edit") {
          return;
        }
        this.name = newVal.name || "";
        this.description = newVal.description || "";
        this.color = newVal.color || "#6c757d";
        this.is_crm_label = !!Number(newVal.is_crm_label);
        this.is_business_label = !!Number(newVal.is_business_label);
        this.is_company_label = !!Number(newVal.is_company_label);
        this.active = newVal.active === undefined ? true : !!Number(newVal.active);
      },
    },
  },
  computed: {
    modalTitle() {
      return this.mode === "edit" ? "Edit Label" : "Add Label";
    },
    modalId() {
      return this.mode === "edit" ? "edit_label" : "add_label";
    },
  },
  methods: {
    buildPayload() {
      return {
        name: this.name,
        description: this.description,
        color: this.color || null,
        is_crm_label: this.is_crm_label ? 1 : 0,
        is_business_label: this.is_business_label ? 1 : 0,
        is_company_label: this.is_company_label ? 1 : 0,
        active: this.active ? 1 : 0,
        csrf_token: this.csrf_token,
      };
    },
    async submitLabel() {
      this.tryingToSubmit = true;
      this.submitted = true;
      this.$v.$touch();

      if (this.$v.$invalid) {
        this.tryingToSubmit = false;
        return;
      }

      try {
        if (this.mode === "edit") {
          await Customers.updateCustomersLabel(this.labelData.id, this.buildPayload());
          this.successmsg("Label updated successfully");
        } else {
          await Customers.createCustomersLabel(this.buildPayload());
          this.successmsg("Label added successfully");
        }
        this.closeModal();
        this.refreshData();
      } catch (error) {
        const err = error.response?.data?.error;
        this.failedmsg(Array.isArray(err) ? err.join(", ") : err || "Request failed");
      } finally {
        this.tryingToSubmit = false;
      }
    },
    resetProps() {
      this.name = "";
      this.description = "";
      this.color = "#6c757d";
      this.is_crm_label = false;
      this.is_business_label = false;
      this.is_company_label = false;
      this.active = true;
      this.submitted = false;
      this.tryingToSubmit = false;
      this.$v.$reset();
    },
    refreshData() {
      this.$emit("onRefresh");
    },
    closeModal() {
      this.showModal = false;
    },
  },
};
</script>

<template>
  <b-modal
    v-model="showModal"
    :id="modalId"
    :title="modalTitle"
    title-class="font-18"
    centered
    @hidden="resetProps"
  >
    <form @submit.prevent="submitLabel">
      <b-form-group label="Name" label-for="label-name" class="mb-3">
        <b-form-input
          v-model.trim="name"
          type="text"
          id="label-name"
          :class="{ 'is-invalid': submitted && $v.name.$error }"
        />
        <validationMessages
          v-if="submitted"
          :fieldName="'Name'"
          :validationName="$v.name"
        />
      </b-form-group>

      <b-form-group label="Description" label-for="label-description" class="mb-3">
        <b-form-textarea
          v-model.trim="description"
          id="label-description"
          rows="3"
        />
      </b-form-group>

      <b-form-group label="Color" label-for="label-color" class="mb-3">
        <div class="d-flex align-items-center">
          <b-form-input
            v-model="color"
            type="color"
            id="label-color"
            class="label-color-picker me-2"
          />
          <b-form-input v-model="color" type="text" placeholder="#000000" />
        </div>
      </b-form-group>

      <b-form-group class="mb-2">
        <b-form-checkbox v-model="is_crm_label" switch>CRM label</b-form-checkbox>
      </b-form-group>
      <b-form-group class="mb-2">
        <b-form-checkbox v-model="is_business_label" switch>Business label</b-form-checkbox>
      </b-form-group>
      <b-form-group class="mb-2">
        <b-form-checkbox v-model="is_company_label" switch>Company label</b-form-checkbox>
      </b-form-group>
      <b-form-group class="mb-0">
        <b-form-checkbox v-model="active" switch>Active</b-form-checkbox>
      </b-form-group>

      <input type="hidden" name="csrf_token" v-model="csrf_token" />
    </form>

    <template v-slot:modal-footer>
      <b-button variant="light" @click="closeModal">Close</b-button>
      <b-button variant="primary" @click="submitLabel" :disabled="tryingToSubmit">
        <b-spinner v-show="tryingToSubmit" small />
        {{ mode === "edit" ? "Save" : "Submit" }}
      </b-button>
    </template>
  </b-modal>
</template>

<style scoped>
.label-color-picker {
  width: 48px;
  min-width: 48px;
  padding: 2px;
  height: 38px;
}
</style>
